import React, { useState, useCallback } from 'react';
import * as THREE from 'three';

import { useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeft, Loader2, ScanLine, Zap } from 'lucide-react';

import { simplifyGeometry } from '../../lib/loaders/simplifyGeometry';

import { cn } from '../../lib/utils';

import { countNonManifoldEdges } from '../../lib/countNonManifoldEdges';
import {
  findBoundaryEdges,
  buildRimLoops,
} from '../../tools/thicknessOutward/boundaryUtils';

import { useWorkflowActions, useWorkflowState } from '../../stores/useWorkflowStore';

import {
  WORKFLOW_CONFIG,
  PROCESS_DEFINITIONS,
  PROCESS_LOGO_SRC,
  normalizeWorkflowType,
  getWorkflowTypeForStep,
} from '../../constants/flowConfig';

import useEditorStore from '../../stores/useEditorStore';

import { toast } from 'react-toastify';

import { useStepNavigation } from '../../hooks/useStepNavigation';

import StepBreadcrumb from './StepBreadcrumb';
import BackwardNavDialog from './BackwardNavDialog';
import UndoTruncationDialog from './UndoTruncationDialog';
import SkipStepDialog from './SkipStepDialog';

const NAV_BTN_CLASS =
  'flex min-w-[9.25rem] cursor-pointer items-center justify-center gap-[0.4rem] rounded-md px-[1.15rem] py-[0.55rem] text-sm font-medium touch-manipulation transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-45 max-md:min-w-[8rem] max-md:px-[0.85rem] max-md:py-2 max-md:text-xs motion-reduce:transition-none';

const StepHeader = ({ currentStep, workflowSteps: workflowStepsProp, stepCompletion }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesignProcessRoute = location.pathname.startsWith('/design');

  const { canNavigateToStep } = useWorkflowActions();

  const currentWorkflow = useWorkflowState((state) => state.currentWorkflow);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [meshOptimized, setMeshOptimized] = useState(false);

  const handleOptimizeMesh = useCallback(async () => {
    const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
    const shadedMesh = sceneRefs?.shadedMesh;
    if (!shadedMesh?.geometry) {
      toast.error('No mesh loaded to optimize.');
      return;
    }
    setIsOptimizing(true);
    try {
      const geomToSimplify = shadedMesh.geometry;
      const { geometry: simplified, stats } = await simplifyGeometry(geomToSimplify, {
        source: 'ManualOptimize',
      });
      if (stats.applied) {
        const newMesh = new THREE.Mesh(simplified);
        replaceShadedMesh(newMesh);
        toast.success(
          `Optimized: ${stats.before.triangles.toLocaleString()} → ${stats.after.triangles.toLocaleString()} triangles`
        );
      } else {
        toast.info('Mesh is already fully optimized or cannot be simplified further.');
      }
      setMeshOptimized(true);
    } catch (err) {
      console.error('[StepHeader] Mesh optimization failed:', err);
      toast.error('Mesh optimization failed.');
    } finally {
      setIsOptimizing(false);
    }
  }, []);

  const handleCheckNonManifold = () => {
    const geom = useEditorStore.getState().sceneRefs?.shadedMesh?.geometry;
    if (!geom) {
      toast.error('No socket loaded in this step to check.');
      return;
    }
    const { nonManifoldEdges, openEdges, totalEdges } = countNonManifoldEdges(geom);
    toast.info(
      `Non-manifold edges: ${nonManifoldEdges} · Open edges: ${openEdges} · Total edges: ${totalEdges}`
    );

    // Locate WHERE the open boundary edges are: group them into loops and log
    // each loop's vertex count + world centroid so the hole can be pinpointed.
    const boundary = findBoundaryEdges(geom);
    const loops = buildRimLoops(boundary).filter((l) => l.length >= 1);
    if (!loops.length) {
      console.log('[StepHeader] manifold check: no open boundary loops (watertight)');
    } else {
      const pos = geom.getAttribute('position').array;
      console.log(`[StepHeader] manifold check: ${loops.length} open boundary loop(s), ${boundary.length} open edge(s)`);
      loops
        .map((loop) => {
          const c = new THREE.Vector3();
          for (const vi of loop) c.add(new THREE.Vector3(pos[vi * 3], pos[vi * 3 + 1], pos[vi * 3 + 2]));
          c.divideScalar(loop.length || 1);
          return { verts: loop.length, c };
        })
        .sort((a, b) => b.verts - a.verts)
        .forEach((info, i) => {
          console.log(
            `[StepHeader]   loop ${i}: ${info.verts} verts, `
            + `centroid (${info.c.x.toFixed(2)}, ${info.c.y.toFixed(2)}, ${info.c.z.toFixed(2)})`,
          );
        });
    }
  };

  const {

    handleNext,

    handlePrevious,

    navigateToStep,

    isNextDisabled,

    isPrevDisabled,

    isCheckpointUploading,

    isFinalStep,

  } = useStepNavigation();

  const resolvedWorkflow =
    getWorkflowTypeForStep(currentStep) ?? normalizeWorkflowType(currentWorkflow, currentStep);
  const workflowSteps =
    WORKFLOW_CONFIG[resolvedWorkflow]?.steps?.length > 0
      ? WORKFLOW_CONFIG[resolvedWorkflow].steps
      : workflowStepsProp ?? [];
  const processLabel =
    PROCESS_DEFINITIONS.find((p) => p.id === resolvedWorkflow)?.label ?? 'Design process';

  const handleLogoClick = () => {

    navigate('/dashboard');

  };



  const handleStepClick = (step) => {

    const canNav = canNavigateToStep(step);

    if (canNav) {

      const wf = normalizeWorkflowType(currentWorkflow, currentStep);

      const currentStepHandler = WORKFLOW_CONFIG[wf]?.stepHandlers?.[currentStep]?.onComplete;

      const currentStepIndex = WORKFLOW_CONFIG[wf]?.steps.indexOf(currentStep);

      const targetStepIndex = WORKFLOW_CONFIG[wf]?.steps.indexOf(step);

      const isNextStep = targetStepIndex === currentStepIndex + 1;

      navigateToStep(step, {
        isNextStepWithHandler: !!(currentStepHandler && isNextStep),
      });

    } else {

      toast.error('This step is not accessible before the previous step is completed.');

    }

  };



  return (

    <header
      className={cn(
        'flex shrink-0 flex-row items-center justify-between gap-6 bg-white pl-6.5 pr-1.5 py-3 [tap-highlight-color:transparent] max-md:flex-col max-md:items-stretch motion-reduce:transition-none',
        !isDesignProcessRoute && 'border-b border-black/[0.08]'
      )}
    >

      <div className="flex min-w-0 flex-1 items-center gap-4 max-md:w-full max-md:flex-col max-md:items-stretch max-md:gap-2">

        <button

          type="button"

          className="flex h-9 shrink-0 cursor-pointer items-center rounded-md border-0 bg-transparent p-0 touch-manipulation [tap-highlight-color:transparent] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--focus-ring,#0070f3)] focus-visible:outline-offset-2 max-md:self-start"

          onClick={handleLogoClick}

          aria-label={`${processLabel} — go to dashboard`}

        >

          <img

            src={PROCESS_LOGO_SRC}

            alt=""

            width={30}

            height={30}

            decoding="async"

            className="block h-7.5 w-7.5 object-contain"

          />

        </button>



        <StepBreadcrumb
          processLabel={processLabel}
          workflowSteps={workflowSteps}
          currentStep={currentStep}
          stepCompletion={stepCompletion}
          canNavigateToStep={canNavigateToStep}
          onStepClick={handleStepClick}
        />

      </div>



      <div className="flex shrink-0 items-center gap-[0.65rem] max-md:w-full max-md:justify-end max-[480px]:justify-center">

        <button
          type="button"
          className={cn(
            NAV_BTN_CLASS,
            'border border-black/10 bg-white group text-neutral-700 hover:bg-[#fafafa] flex items-center gap-2'
          )}
          onClick={handleCheckNonManifold}
          title="Check non-manifold edges of the current socket"
        >
          <ScanLine size={14} strokeWidth={2} aria-hidden className="shrink-0" />
          <div>Check edges</div>
        </button>
{/* 
        <button
          type="button"
          className={cn(
            NAV_BTN_CLASS,
            'border border-black/10 bg-white group text-neutral-700 hover:bg-[#fafafa] flex items-center gap-2'
          )}
          onClick={handleOptimizeMesh}
          disabled={isOptimizing || meshOptimized}
          title={meshOptimized ? 'Mesh already optimized' : 'Optimize mesh using meshoptimizer'}
        >
          {isOptimizing ? (
            <Loader2 size={14} className="odp-loader-spin shrink-0" aria-hidden />
          ) : (
            <Zap size={14} strokeWidth={2} aria-hidden className="shrink-0" />
          )}
          <div>{isOptimizing ? 'Optimizing…' : 'Optimize'}</div>
        </button> */}

        <button
          type="button"
          className={cn(
            NAV_BTN_CLASS,
            'border border-black/10 bg-white group text-neutral-700 hover:bg-[#fafafa] flex items-center gap-2'
          )}
          onClick={handlePrevious}
          title="Go to previous step"
          disabled={isPrevDisabled}
        >
          <ArrowLeft size={12} strokeWidth={2} aria-hidden className="group-hover:-translate-x-2 shrink-0 group-hover:opacity-100 transition-all duration-200 ease-in-out" />
          <div>Previous</div>
        </button>

        <button
          type="button"
          className={cn(
            NAV_BTN_CLASS,
            'border border-transparent bg-odp-purple text-white hover:bg-odp-purple-dark group',
            (isNextDisabled || isCheckpointUploading) && 'pointer-events-none',
            isCheckpointUploading && 'cursor-wait'
          )}
          onClick={handleNext}
          title={isFinalStep ? 'Finish and save the design' : 'Go to next step'}
          disabled={isNextDisabled}
          aria-busy={isCheckpointUploading}
          aria-disabled={isNextDisabled || isCheckpointUploading}
        >
          <div>{isFinalStep ? (isCheckpointUploading ? 'Finishing…' : 'Finish') : 'Next'}</div>
          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
            {isCheckpointUploading ? (
              <Loader2 size={16} className="odp-loader-spin" />
            ) : isFinalStep ? null : (
              <ArrowLeft size={12} strokeWidth={2} aria-hidden className="group-hover:translate-x-2 shrink-0 transition-all duration-200 ease-in-out rotate-180" />
            )}
          </span>
        </button>

      </div>

      <BackwardNavDialog />
      <UndoTruncationDialog />

      <SkipStepDialog />

    </header>

  );

};



export default StepHeader;

